import {
  BasicWidgetFormComponent,
  WidgetDialogComponent,
  WidgetDialogProps,
} from "@/features/widget-forms/dialogs/widget-dialog-types";
import { BasicWidgetDialogShell } from "@/features/widget-forms/dialogs/basic-widget-dialog-shell";
import { FormValues, WidgetTypes } from "@/types/widget";
import DialogFooter from "@/components/dialog/dialog-footer";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import { LegacyWidgetForm } from "@/features/widget-forms/forms/legacy-widget-form";
import { MapWidgetForm } from "@/features/widget-forms/forms/map-widget-form";
import { ObservationsMapWidgetDialog } from "@/features/widget-forms/forms/observations-map/observations-map-widget-dialog";
import { RichTextWidgetForm } from "@/features/widget-forms/forms/richtext-widget-form";
import { LineGraphForm } from "@/features/widget-forms/forms/line-graph-form";
import { OpmetReportWidgetForm } from "@/features/widget-forms/forms/opmet/report-widget-form";
import { EarlyWarningTableWidgetForm } from "@/features/widget-forms/forms/early-warning-widget-form";
import Dialog from "@/components/dialog/dialog";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import DialogHeader from "@/components/dialog/dailog-header";
import { WeatherModelMapDialog } from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog";
import { MaritimeBroadcastWidgetForm } from "@/features/widget-forms/forms/maritime-broadcast-widget-form";
import { MaritimeForecastWidgetForm } from "@/features/widget-forms/forms/maritime-forecast-widget-form";
import { MaritimeSeichesWidgetForm } from "@/features/widget-forms/forms/maritime-seiches-widget-form";
import { TimeSeriesWidgetDialog } from "@/features/widget-forms/forms/time-series/time-series-widget-dialog";
import { normalizeMaritimeWidgetFormValues } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { AdminErrorBoundary } from "@/features/admin/components/error-boundary/admin-error-boundary";

const widgetDialogs: Record<WidgetTypes, WidgetDialogComponent> = {
  [WidgetTypes.LEGACY]: defineBasicWidgetDialog(WidgetTypes.LEGACY, LegacyWidgetForm),
  [WidgetTypes.MAP]: defineBasicWidgetDialog(WidgetTypes.MAP, MapWidgetForm),
  [WidgetTypes.RICH_TEXT]: defineBasicWidgetDialog(
    WidgetTypes.RICH_TEXT,
    RichTextWidgetForm,
  ),
  [WidgetTypes.LINE_GRAPH]: defineBasicWidgetDialog(WidgetTypes.LINE_GRAPH, LineGraphForm),
  [WidgetTypes.OPMET_REPORT]: defineBasicWidgetDialog(
    WidgetTypes.OPMET_REPORT,
    OpmetReportWidgetForm,
  ),
  [WidgetTypes.EARLY_WARNING_TABLE]: defineBasicWidgetDialog(
    WidgetTypes.EARLY_WARNING_TABLE,
    EarlyWarningTableWidgetForm,
  ),
  [WidgetTypes.OBSERVATIONS_MAP]: ObservationsMapWidgetDialog,
  [WidgetTypes.MODEL_MAP]: WeatherModelMapDialog,
  [WidgetTypes.MARITIME_BROADCAST]: defineBasicWidgetDialog(
    WidgetTypes.MARITIME_BROADCAST,
    MaritimeBroadcastWidgetForm,
    normalizeMaritimeWidgetFormValues,
  ),
  [WidgetTypes.MARITIME_FORECAST]: defineBasicWidgetDialog(
    WidgetTypes.MARITIME_FORECAST,
    MaritimeForecastWidgetForm,
    normalizeMaritimeWidgetFormValues,
  ),
  [WidgetTypes.MARITIME_SEICHES]: defineBasicWidgetDialog(
    WidgetTypes.MARITIME_SEICHES,
    MaritimeSeichesWidgetForm,
    normalizeMaritimeWidgetFormValues,
  ),
  [WidgetTypes.TIME_SERIES]: TimeSeriesWidgetDialog,
};

function defineBasicWidgetDialog(
  type: WidgetTypes,
  MyFormComponent: BasicWidgetFormComponent,
  transformValues?: (values: FormValues) => FormValues,
): WidgetDialogComponent {
  return function BasicWidgetDialog({
    initialValues,
    open,
    onOpenChange,
    onSubmit,
    onReset,
  }: WidgetDialogProps) {
    // Returned so final-form keeps `submitting` true for the whole request and
    // the footer can show its saving state.
    const handleSubmit = (values: FormValues) =>
      onSubmit(transformValues ? transformValues(values) : values);

    return (
      <BasicWidgetDialogShell
        type={type}
        open={open}
        onOpenChange={onOpenChange}
        onReset={onReset}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, errors }) => <MyFormComponent values={values} errors={errors} />}
      </BasicWidgetDialogShell>
    );
  };
}

export interface DynamicWidgetDialogProps extends WidgetDialogProps {
  type: WidgetTypes;
}

export function DynamicWidgetDialog({
  type,
  open,
  onOpenChange,
  onReset,
  onSubmit,
  initialValues,
  isCreate,
}: DynamicWidgetDialogProps) {
  const WidgetDialogComponent = widgetDialogs[type];
  if (!WidgetDialogComponent) {
    return (
      <UnsupportedWidgetDialog
        type={type}
        open={open}
        onOpenChange={onOpenChange}
        onReset={onReset}
      />
    );
  }
  return (
    <AdminErrorBoundary
      tags={{ component: "WidgetDialog", widgetType: type }}
      fallback={(error) => (
        <WidgetDialogErrorFallback
          error={error}
          open={open}
          onOpenChange={onOpenChange}
          onReset={onReset}
        />
      )}
    >
      <WidgetDialogComponent
        initialValues={initialValues}
        open={open}
        onOpenChange={onOpenChange}
        onSubmit={onSubmit}
        onReset={onReset}
        isCreate={isCreate}
      />
    </AdminErrorBoundary>
  );
}

function WidgetDialogErrorFallback({
  error,
  open,
  onOpenChange,
  onReset,
}: {
  error: Error;
} & Pick<DynamicWidgetDialogProps, "open" | "onOpenChange" | "onReset">) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} onClose={onReset}>
      <Dialog.Content className={dialogVariants.default}>
        <DialogHeader.Root>
          <DialogHeader.Title>
            {t("admin.error_boundary.widget_dialog_title")}
          </DialogHeader.Title>
        </DialogHeader.Root>
        <p>{error?.message || t("admin.error_boundary.description")}</p>
        <DialogFooter>
          <Stack justifyContent="flex-end" gap={8}>
            <Button key="close" variant="card" onClick={onReset}>
              {t("general.close")}
            </Button>
          </Stack>
        </DialogFooter>
      </Dialog.Content>
    </Dialog>
  );
}

function UnsupportedWidgetDialog({
  type,
  open,
  onOpenChange,
  onReset,
}: Pick<DynamicWidgetDialogProps, "type" | "open" | "onOpenChange" | "onReset">) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} onClose={onReset}>
      <Dialog.Content className={dialogVariants.default}>
        <DialogHeader.Root>
          <DialogHeader.Title>Unsupported widget type</DialogHeader.Title>
        </DialogHeader.Root>
        <p>
          Widget of type &quot;<code>{type}</code>&quot; not supported.
        </p>
        <DialogFooter>
          <Stack justifyContent="flex-end" gap={8}>
            <Button key="save" variant="card" onClick={onReset}>
              {t("general.close")}
            </Button>
          </Stack>
        </DialogFooter>
      </Dialog.Content>
    </Dialog>
  );
}
